import React, { useState, useEffect } from 'react';
import {
  Alert, Typography, Grid, Card, CardContent, Button, CircularProgress
} from '@mui/material';
import { cameraService, CameraPhoto, CameraPhotoPage } from '../services/cameraService';
import { API_URL } from '../config/api';
import AuthenticatedImage from './AuthenticatedImage';

const CameraImage = ({
  photo,
  priority = 'normal',
  height = 180,
}: {
  photo: CameraPhoto;
  priority?: 'high' | 'normal';
  height?: number;
}) => {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  if (failed) {
    return (
      <div style={{ height, display: 'grid', placeItems: 'center', background: '#ffebee', padding: 16 }}>
        <Typography color="error" align="center" variant="body2">
          Image failed to load: {photo.file_name}
        </Typography>
      </div>
    );
  }
  return (
    <div style={{ height, position: 'relative', background: '#eef2f6' }}>
      {!loaded && (
        <Typography
          variant="body2"
          color="text.secondary"
          style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}
        >
          Loading image…
        </Typography>
      )}
      <AuthenticatedImage
        src={`${API_URL}/camera/image?cam_id=${encodeURIComponent(photo.cam_id)}&file_name=${encodeURIComponent(photo.file_name)}`}
        alt={photo.file_name}
        style={{ width: '100%', height, objectFit: 'cover', position: 'relative' }}
        requestPriority={priority}
        loading={priority === 'high' ? 'eager' : 'lazy'}
        onLoad={() => setLoaded(true)}
        onLoadError={() => setFailed(true)}
      />
    </div>
  );
};

const CameraPanel: React.FC = () => {
  const [cameras, setCameras] = useState<string[]>([]);
  const [latestPhotos, setLatestPhotos] = useState<CameraPhoto[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string | null>(null);
  const [photoPage, setPhotoPage] = useState<CameraPhotoPage | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingCameras, setLoadingCameras] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [latestError, setLatestError] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);

  // Fetch cameras and latest photos on mount
  useEffect(() => {
    setLoadingCameras(true);
    setCameraError(null);
    setLatestError(null);
    Promise.allSettled([
      cameraService.getSources(),
      cameraService.getLatestPhotos()
    ])
      .then(([cameraResult, latestResult]) => {
        if (cameraResult.status === 'fulfilled') {
          setCameras(cameraResult.value);
        } else {
          setCameras([]);
          setCameraError(cameraResult.reason instanceof Error ? cameraResult.reason.message : 'Failed to load camera list');
        }
        if (latestResult.status === 'fulfilled') {
          setLatestPhotos(latestResult.value);
        } else {
          setLatestPhotos([]);
          setLatestError(latestResult.reason instanceof Error ? latestResult.reason.message : 'Failed to load latest photos');
        }
      })
      .finally(() => setLoadingCameras(false));
  }, []);

  // Fetch paginated photos when a camera is selected or page changes
  useEffect(() => {
    if (!selectedCamera) return;
    setLoading(true);
    setPhotoError(null);
    cameraService.getPhotos(selectedCamera, page, 8)
      .then(setPhotoPage)
      .catch((loadError) => {
        setPhotoPage(null);
        setPhotoError(loadError instanceof Error ? loadError.message : 'Failed to load photos for this camera');
      })
      .finally(() => setLoading(false));
  }, [selectedCamera, page]);

  // Helper to get the latest photo for a camera
  const getLatestPhotoForCamera = (cam_id: string) =>
    latestPhotos.find(photo => photo.cam_id === cam_id);

  // Render the tile grid
  if (!selectedCamera) {
    return (
      <div>
        <Typography variant="h5" gutterBottom>Camera List</Typography>
        {loadingCameras ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
            <CircularProgress />
            <Typography variant="body2" style={{ marginTop: 16 }}>
              Loading camera data from /camera/cameras and /camera/latest...
            </Typography>
          </div>
        ) : cameraError ? (
          <Alert severity="error" sx={{ mb: 2 }}>{cameraError}</Alert>
        ) : (
          <>
            {latestError && <Alert severity="warning" sx={{ mb: 2 }}>{latestError}</Alert>}
            {cameras.length === 0 ? (
              <Alert severity="info">No cameras are available.</Alert>
            ) : <Grid container spacing={3}>
              {cameras.map(cam_id => {
              const photo = getLatestPhotoForCamera(cam_id);
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={cam_id}>
                  <Card sx={{ cursor: 'pointer' }} onClick={() => { setSelectedCamera(cam_id); setPage(1); }}>
                    {photo ? (
                      <CameraImage photo={photo} priority="high" />
                    ) : (
                      <div style={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#eee' }}>
                        <Typography variant="body2">No image</Typography>
                      </div>
                    )}
                    <CardContent>
                      <Typography variant="h6">{cam_id}</Typography>
                      {photo && (
                        <Typography variant="body2" color="textSecondary">
                          {new Date(photo.timestamp).toLocaleString()}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
              })}
            </Grid>}
          </>
        )}
      </div>
    );
  }

  // Render the paginated gallery for a selected camera
  return (
    <div>
      <Button onClick={() => setSelectedCamera(null)} sx={{ mb: 2 }}>&larr; Back to camera list</Button>
      <Typography variant="h5" gutterBottom>Photos for {selectedCamera}</Typography>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', margin: '32px 0' }}>
          <CircularProgress />
        </div>
      ) : photoError ? (
        <Alert severity="error" sx={{ mb: 2 }}>{photoError}</Alert>
      ) : photoPage && photoPage.photos.length > 0 ? (
        <>
          <Grid container spacing={3}>
            {photoPage.photos.map(photo => (
              <Grid item xs={12} sm={6} md={3} key={photo.timestamp + photo.file_name}>
                <Card>
                  <CameraImage photo={photo} height={220} />
                  <CardContent>
                    <Typography variant="body2">{photo.file_name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {new Date(photo.timestamp).toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <div style={{ display: 'flex', justifyContent: 'center', margin: '24px 0' }}>
            <Button
              variant="outlined"
              disabled={page <= 1 || loading}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              ← Previous
            </Button>
            <Typography sx={{ mx: 2, alignSelf: 'center' }}>
              Page {page} of {Math.ceil(photoPage.total / photoPage.limit)}
            </Typography>
            <Button
              variant="outlined"
              disabled={
                page >= Math.ceil(photoPage.total / photoPage.limit) || loading
              }
              onClick={() => setPage((current) => current + 1)}
            >
              Next →
            </Button>
          </div>
        </>
      ) : (
        <Typography>No photos found for this camera.</Typography>
      )}
    </div>
  );
};

export default CameraPanel; 